import useBracketStore from '@/stores/useBracketStore'

// SVG Connector Lines Component for bracket visualization
export default function BracketConnectorLines() {
  const bracketStructure = useBracketStore(s => s.bracketStructure)
  const viewMode = useBracketStore(s => s.viewMode)
  const matchCellInfo = useBracketStore(s => s.matchCellInfo)
  const bracket = useBracketStore(s => s.bracket)
  const teamSize = bracket?.divisionTeamSize || 1

  const isCompact = viewMode === 'compact'

  // Don't draw lines in compact view
  if (isCompact) return null

  const CELL_H = 40 + Math.max(0, teamSize - 1) * 24   // 40 singles, 64 doubles
  const MATCH_HEIGHT = 38 + 2 * CELL_H                  // 118 singles, 166 doubles
  const MATCH_SPACING = 40
  const CARD_WIDTH = 280
  const ROUND_WIDTH = 320
  const ROUND_GAP = 32
  // Match-card layout (BracketMatchCard): a fixed-height header + N cells
  // distributed evenly via `flex-1 basis-0`. MATCH_HEIGHT = 38 + N * CELL_H,
  // so the header is exactly 38px and each cell is exactly CELL_H. The
  // divider between top and bottom team sections is therefore at
  //   header + cellsAbove * CELL_H
  // where cellsAbove is the number of cells in the top section.
  // The card itself enforces this 38px via `min-h-[38px]` on its header div.
  const CARD_HEADER_HEIGHT = 38
  const baseStep = MATCH_HEIGHT + MATCH_SPACING

  const hasPQ = bracketStructure.some(r => r.roundNum === 0)

  // Calculate positions for all matches
  const matchPositions = new Map()
  const round1Step = hasPQ ? baseStep * 2 : baseStep
  const round1TopOffset = hasPQ ? baseStep / 2 : 0

  bracketStructure.forEach((round, roundIndex) => {
    const { roundNum, matches: roundMatches } = round
    const effectiveRound = hasPQ && roundNum >= 1 ? roundNum + 1 : roundNum
    const roundStep = roundNum === 0 ? baseStep : baseStep * Math.pow(2, effectiveRound - 1)
    const roundTopOffset = roundNum === 0 ? 0 : (baseStep * (Math.pow(2, effectiveRound - 1) - 1)) / 2
    const xPosition = roundIndex * (ROUND_WIDTH + ROUND_GAP)

    roundMatches.forEach(match => {
      let topPosition = 0
      if (roundNum === 0) {
        const feedsIntoMatchNum = match.feeds_into_match_number || match.match_number
        const feedsIntoSlot = match.feeds_into_slot || 1
        const round1MatchPosition = round1TopOffset + (feedsIntoMatchNum - 1) * round1Step
        const slotOffset = feedsIntoSlot === 1 ? 0 : baseStep
        topPosition = round1MatchPosition + slotOffset
      } else {
        topPosition = roundTopOffset + (match.match_number - 1) * roundStep
      }

      // pos.y is the card's top edge in the SVG's coordinate system. The SVG
      // is positioned at top:0 of the same wrapper that contains the round
      // columns, and cards are absolutely positioned at `top: ${topPosition}`
      // inside their column. Both share the same y-origin, so pos.y maps 1:1
      // to topPosition. Earlier code added a stale HEADER_HEIGHT of 40 here —
      // that offset was needed back when round labels lived inside the scroll
      // container, but the labels are now in a sibling sticky div outside it
      // (see DesktopBracketView). The leftover offset dragged every connector
      // ~40px below the team-row divider, so they appeared to emerge from the
      // bottom edge of the cards.
      matchPositions.set(match.match_id, {
        x: xPosition,
        y: topPosition,
        width: CARD_WIDTH,
        height: MATCH_HEIGHT,
        round: roundNum,
        matchNumber: match.match_number,
        feedsIntoRound: match.feeds_into_round,
        feedsIntoMatchNumber: match.feeds_into_match_number,
        feedsIntoSlot: match.feeds_into_slot
      })
    })
  })

  // Generate connector lines with squared-off paths
  const lines = []
  const allMatches = bracketStructure.flatMap(r => r.matches)

  // Helper to calculate divider position (between top team section and bottom team section).
  //
  // Card layout (BracketMatchCard.jsx):
  //   - Header: exactly 38px (CARD_HEADER_HEIGHT)
  //   - Cells: flex-1 basis-0, each rendered exactly CELL_H tall because the
  //     parent's min-height is set to `38 + N*CELL_H`.
  //
  // The divider sits at `header + cellsAbove * CELL_H`, where cellsAbove counts
  // every cell in the top team section: 1 for the standard 2-cell card (cell1
  // above the divider, cell4 below), 2 when cell2 is visible (an "actual winner"
  // row inserted between cell1 and the divider). cell3 (actual winner of the
  // bottom slot) lives below the divider and doesn't shift it.
  //
  // DUB-70: the previous SVG_DIVIDER_BASE of 58.5 was just a wrong constant —
  // it didn't match the actual header height (38). Combined with various
  // cellsAbove formulas the line landed in the middle of cell1 or middle of
  // cell4 depending on which "fix" was in place. Using the actual header
  // height makes the math self-consistent across singles, doubles, and
  // cell2-visible variants.
  const getDividerY = (matchId, pos) => {
    const cellInfo = matchCellInfo?.get(matchId)
    const cellsAbove = cellInfo?.cell2Visible ? 2 : 1
    return pos.y + CARD_HEADER_HEIGHT + cellsAbove * CELL_H
  }

  allMatches.forEach(match => {
    const fromPos = matchPositions.get(match.match_id)
    if (!fromPos) return

    let targetMatch = null
    if (match.round === 0) {
      if (match.feeds_into_round && match.feeds_into_match_number) {
        targetMatch = allMatches.find(m => m.round === match.feeds_into_round && m.match_number === match.feeds_into_match_number)
      }
    } else {
      const nextRound = match.round + 1
      const nextMatchNumber = Math.ceil(match.match_number / 2)
      targetMatch = allMatches.find(m => m.round === nextRound && m.match_number === nextMatchNumber)
    }

    if (!targetMatch) return
    const toPos = matchPositions.get(targetMatch.match_id)
    if (!toPos) return

    // Lines run from the source card's right edge to the target card's left
    // edge, never passing behind the card body. The cards use a translucent
    // glassmorphism background, so routing lines through card centers (the
    // earlier approach) made the connectors visible *through* the card —
    // cosmetically broken even when the math was right.
    const fromX = fromPos.x + fromPos.width
    const fromY = getDividerY(match.match_id, fromPos)
    const toX = toPos.x
    const toY = getDividerY(targetMatch.match_id, toPos)

    // Vertical connector sits in the gap between rounds at the midpoint.
    const gapMidX = fromX + (toX - fromX) / 2
    const path = `M ${fromX} ${fromY} L ${gapMidX} ${fromY} L ${gapMidX} ${toY} L ${toX} ${toY}`

    lines.push({
      id: `${match.match_id}-to-${targetMatch.match_id}`,
      path
    })
  })

  // SVG width = sum of round-column widths + gaps between them. The SVG sits
  // inside the rounds-flex (see BracketRoundRenderer), with its left edge
  // anchored to column 0's left edge. Earlier this was n*(ROUND_WIDTH +
  // ROUND_GAP), which over-counted by one trailing ROUND_GAP and made the
  // SVG box wider than the rounds-flex.
  const roundCount = bracketStructure.length
  const totalWidth = roundCount * ROUND_WIDTH + Math.max(0, roundCount - 1) * ROUND_GAP
  let maxHeight = 0
  matchPositions.forEach(pos => {
    const bottom = pos.y + pos.height
    if (bottom > maxHeight) maxHeight = bottom
  })
  const totalHeight = maxHeight + 100

  return (
    <svg
      className="absolute top-0 pointer-events-none"
      width={totalWidth}
      height={totalHeight}
      style={{ zIndex: 0 }}
    >
      {lines.map(line => (
        <path
          key={line.id}
          d={line.path}
          fill="none"
          stroke="var(--mauve-7)"
          strokeWidth="1"
        />
      ))}
    </svg>
  )
}
